import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

export default function GuestbookModerator() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, entRes, statsRes] = await Promise.all([
        api.get(`/invitations/${id}`),
        api.get(`/invitations/${id}/guestbook`, { params: filter === "all" ? {} : { status: filter } }),
        api.get(`/invitations/${id}/guestbook/stats`),
      ]);
      setInv(invRes.data);
      setEntries(entRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load guestbook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, filter]);

  const moderate = async (entryId, status) => {
    try {
      await api.patch(`/invitations/${id}/guestbook/${entryId}`, { status });
      toast.success(status === "approved" ? "Approved" : status === "rejected" ? "Rejected" : "Updated");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const del = async (entryId) => {
    if (!window.confirm("Permanently delete this entry?")) return;
    try {
      await api.delete(`/invitations/${id}/guestbook/${entryId}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleEnabled = async () => {
    if (!inv) return;
    const newValue = !(inv.data?.guestbook_enabled !== false);
    try {
      await api.put(`/invitations/${id}`, { data: { ...inv.data, guestbook_enabled: newValue } });
      toast.success(newValue ? "Guestbook enabled" : "Guestbook disabled");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const enabled = inv?.data?.guestbook_enabled !== false;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="guestbook-moderator-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-10">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.3em] text-stone-500 hover:text-stone-900">← Back to dashboard</Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-2">Guestbook · Photo-wall</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light tracking-tight">{inv?.title || "Loading…"}</h1>
            <div className="text-xs text-stone-500 mt-2 uppercase tracking-[0.2em]">/{inv?.slug}</div>
          </div>
          <button onClick={toggleEnabled} data-testid="guestbook-toggle-btn" className={`px-5 py-2 text-xs uppercase tracking-[0.3em] border ${enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-stone-800 border-stone-400"}`}>
            {enabled ? "Guestbook ON · click to disable" : "Guestbook OFF · click to enable"}
          </button>
        </div>

        {/* Share deep link */}
        {inv?.slug && inv?.paid && enabled && (
          <div className="mb-6 bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3" data-testid="guestbook-share-deeplink">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-1">Deep-link · skips intro, jumps straight to the wall</div>
              <code className="text-sm text-stone-800 break-all">{window.location.origin}/i/{inv.slug}#guestbook</code>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/i/${inv.slug}#guestbook`); toast.success("Link copied"); }} data-testid="guestbook-copy-link" className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.3em] hover:bg-stone-800">Copy</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="guestbook-stats">
          <StatTile label="Total" value={stats.total} color="#1A1918" />
          <StatTile label="Pending" value={stats.pending} color="#D97706" />
          <StatTile label="Approved" value={stats.approved} color="#059669" />
          <StatTile label="Rejected" value={stats.rejected} color="#B91C1C" />
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["pending", "approved", "rejected", "all"].map((k) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`guestbook-filter-${k}`} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${filter === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-900"}`}>
              {k === "all" ? "All" : k} {filter === k ? `(${entries.length})` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-stone-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="border border-dashed border-stone-300 p-16 text-center">
            <div className="font-serif-display text-2xl mb-2">No {filter === "all" ? "" : filter} entries yet.</div>
            <div className="text-stone-500 text-sm">Guest messages will show up here as they come in.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {entries.map((e) => (
              <article key={e.id} className="bg-white border border-stone-200 p-5 flex flex-col" data-testid={`mod-entry-${e.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-serif-display text-xl">{e.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mt-1">
                      {new Date(e.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${e.status === "approved" ? "bg-emerald-100 text-emerald-800" : e.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                    {e.status}
                  </span>
                </div>
                {e.photo_url && (
                  <img src={e.photo_url.startsWith("/") ? `${BACKEND_URL}${e.photo_url}` : e.photo_url} alt="" className="w-full aspect-video object-cover mb-3 bg-stone-100" />
                )}
                <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line flex-1">{e.message}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {e.status !== "approved" && (
                    <button onClick={() => moderate(e.id, "approved")} data-testid={`mod-approve-${e.id}`} className="flex-1 py-2 bg-emerald-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-emerald-700">Approve</button>
                  )}
                  {e.status !== "rejected" && (
                    <button onClick={() => moderate(e.id, "rejected")} data-testid={`mod-reject-${e.id}`} className="flex-1 py-2 bg-stone-200 text-stone-800 text-xs uppercase tracking-[0.2em] hover:bg-stone-300">Reject</button>
                  )}
                  <button onClick={() => del(e.id)} data-testid={`mod-delete-${e.id}`} className="py-2 px-3 border border-red-300 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-50">Del</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div className="border border-stone-200 p-4 bg-white">
      <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{label}</div>
      <div className="font-serif-display text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
